import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Plus, Trash2, Edit2, HelpCircle, X, Check, ClipboardList, ClipboardPaste, ChevronLeft, ChevronRight, SlidersHorizontal, Search } from 'lucide-react';
import { useBudget } from '../../context/BudgetContext';
import { formatCurrency, formatDate, todayStr, yesterdayStr, uuid } from '../../utils/formatting';
import { resolveCategoryName, computeBalanceCostBasis } from '../../utils/calculations';
import { ConfirmDialog, EmptyState, Modal, CategorySelect, CardList, ListRow } from '../Common/Common';
import PasteImport from './PasteImport';
import { getAllCurrencies, getCurrencyInfo } from '../../utils/currencies';
import { SearchableSelect } from '../Common/Common';
import { submitTransaction as runSubmit } from '../../utils/submitTransaction';
import { ensureRatesForRange, getLatestRate } from '../../utils/exchangeRates';
import { useIsPhone } from '../../hooks/useIsPhone';

function Tracker() {
  const { categories, transactions, buckets, yearlyExpenses, addTransaction, addTransactions, updateTransaction, deleteTransaction,
    addYearlyExpense, currencyBalances, deductFromCurrencyBalance, addCurrencyLot,
    settings, showToast, recurringTemplates, addRecurringTemplate, updateRecurringTemplate, deleteRecurringTemplate,
    openEntrySheet } = useBudget();
  const mainCurrency = settings.currency || 'ILS';
  const fmt = v => formatCurrency(v, settings);

  const emptyForm = {
    date: todayStr(), type: 'expense', category: '', description: '', amount: '', bucket: '',
    currency: mainCurrency, exchangeRate: '', fromBalance: false, tags: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [filter, setFilter] = useState({ type: String(), currency: String(), search: String(), dateFrom: String(), dateTo: String() });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editId, setEditId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showPasteImport, setShowPasteImport] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  // Count of active non-search filters (search has its own bar on phone)
  const activeFilterCount = [filter.type, filter.currency, filter.dateFrom, filter.dateTo].filter(Boolean).length;

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkRecatId, setBulkRecatId] = useState('');
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleSelectAll() {
    if (selected.size === visibleTxns?.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleTxns?.map(t => t.id) || []));
    }
  }
  function clearSelection() { setSelected(new Set()); setBulkRecatId(''); }

  const dateRef = useRef();
  const amountRef = useRef();

  function setF(k, v) { setForm(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); }

  const cancelEdit = useCallback(() => { setForm(emptyForm); setEditId(null); setErrors({}); }, []);

  // Keyboard shortcuts
  const handleGlobalKey = useCallback((e) => {
    const tag = e.target.tagName;
    const isInput = ['INPUT', 'SELECT', 'TEXTAREA'].includes(tag);

    if (e.key === 'Escape') {
      if (editId) cancelEdit();
      else { setForm(emptyForm); setErrors({}); }
      return;
    }
    if (isInput && e.target.id === 'date-input') {
      if (e.key === 't' || e.key === 'T') { e.preventDefault(); setF('date', todayStr()); }
      if (e.key === 'y' || e.key === 'Y') { e.preventDefault(); setF('date', yesterdayStr()); }
    }
    if (!isInput) {
      if (e.key === '1') setF('type', 'income');
      if (e.key === '2') setF('type', 'expense');
      if (e.key === '3') setF('type', 'savings');
    }
    // Alt shortcuts for categories
    if (e.altKey && !isInput) {
      const shortcuts = { g: 'groceries', t: 'transport', u: 'utilities', f: 'dining', e: 'entertainment' };
      const catId = shortcuts[e.key.toLowerCase()];
      if (catId && categories.find(c => c.id === catId)) {
        e.preventDefault();
        setF('category', catId);
      }
    }
  }, [categories, editId, cancelEdit]);

  useEffect(() => {
    if (showPasteImport) return;
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [handleGlobalKey, showPasteImport]);

  // When a foreign-currency savings bucket is selected, prefill the exchange
  // rate from the FX cache (fetching if not yet known). Never overwrites a
  // user-entered rate or balance-sourced flow.
  useEffect(() => {
    if (form.type !== 'savings' || !form.bucket) return;
    const picked = buckets.find(b => b.id === form.bucket);
    if (!picked?.currency || picked.currency === mainCurrency) return;
    if (form.fromBalance) return;
    if (form.exchangeRate !== '' && form.exchangeRate != null) return;
    const todayYM = new Date().toISOString().slice(0, 7);
    (async () => {
      await ensureRatesForRange(picked.currency, mainCurrency, todayYM, todayYM);
      const r = getLatestRate(picked.currency, mainCurrency);
      if (r != null) {
        setForm(p => (p.exchangeRate === '' ? { ...p, exchangeRate: String(r) } : p));
      }
    })();
  }, [form.type, form.bucket, form.fromBalance, form.exchangeRate, buckets, mainCurrency]);

  const isPhone = useIsPhone();

  function submit() {
    const ok = runSubmit(form, {
      settings, currencyBalances, categories,
      addTransaction, updateTransaction, deductFromCurrencyBalance,
      showToast, setErrors, editId,
    });
    if (!ok) return;
    if (editId) setEditId(null);
    setForm(emptyForm);
    setTimeout(() => dateRef.current?.focus(), 50);
  }

  function startEdit(t) {
    const hasForeign = t.currency && t.currency !== mainCurrency && Number.isFinite(t.foreignAmount);
    setForm({
      date: t.date, type: t.type, category: t.category, description: t.description,
      amount: hasForeign ? String(t.foreignAmount) : String(t.amount),
      bucket: t.bucket || '',
      currency: t.currency || mainCurrency,
      exchangeRate: hasForeign && Number.isFinite(t.exchangeRate) ? String(t.exchangeRate) : '',
      fromBalance: !!t.currencyBalanceId || !!t.fromBalance,
      tags: (t.tags || []).join(', '),
    });
    setEditId(t.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Single pass over last 100 transactions → two indexes:
  //   descIndex: { "type:catId" | "type:" → [{ desc, count, rank }] }  — descriptions ranked by freq, used by DescriptionAutocomplete
  //   catIndex:  { "type:desc" | "type:"  → [{ catId, count }] }       — categories ranked by freq for a given description, used by PasteImport
  const { descIndex, catIndex } = useMemo(() => {
    const recent = [...transactions]
      .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id))
      .slice(0, 100);

    const descRaw = {}; // key → { desc → { count, lastDate } }
    const catRaw  = {}; // key → { catId → count }

    recent.forEach(t => {
      if (!t.description || !t.category) return;

      // descIndex: keyed by type:category (scoped) + type: (fallback)
      for (const key of [`${t.type}:${t.category}`, `${t.type}:`]) {
        if (!descRaw[key]) descRaw[key] = {};
        const e = descRaw[key][t.description] || { count: 0, lastDate: t.date };
        e.count++;
        if (t.date > e.lastDate) e.lastDate = t.date;
        descRaw[key][t.description] = e;
      }

      // catIndex: keyed by type:description (scoped) + type: (fallback)
      for (const key of [`${t.type}:${t.description}`, `${t.type}:`]) {
        if (!catRaw[key]) catRaw[key] = {};
        catRaw[key][t.category] = (catRaw[key][t.category] || 0) + 1;
      }
    });

    const descIndex = {};
    Object.entries(descRaw).forEach(([key, map]) => {
      descIndex[key] = Object.entries(map)
        .sort(([, a], [, b]) => b.count - a.count || b.lastDate.localeCompare(a.lastDate))
        .map(([desc, { count }], i) => ({ desc, count, rank: i + 1 }));
    });

    const catIndex = {};
    Object.entries(catRaw).forEach(([key, map]) => {
      catIndex[key] = Object.entries(map)
        .sort(([, a], [, b]) => b - a)
        .map(([catId, count]) => ({ catId, count }));
    });

    return { descIndex, catIndex };
  }, [transactions]);

  // Memoised filter + sort — only recomputed when transactions or filter criteria change
  const filtered = useMemo(() => {
    const catMap = Object.fromEntries(categories.map(c => [c.id, c.name.toLowerCase()]));
    return transactions.filter(t => {
      if (filter.type && t.type !== filter.type) return false;
      if (filter.currency && (t.currency || mainCurrency) !== filter.currency) return false;
      if (filter.search) {
        const q = filter.search.toLowerCase();
        const catName = catMap[t.category] || t.category.toLowerCase();
        const amountStr = String(t.amount);
        const matchesSearch =
          t.description.toLowerCase().includes(q) ||
          catName.includes(q) ||
          amountStr.includes(q) ||
          (t.notes && t.notes.toLowerCase().includes(q)) ||
          (t.tags && t.tags.some(tag => tag.toLowerCase().includes(q)));
        if (!matchesSearch) return false;
      }
      if (filter.dateFrom && t.date < filter.dateFrom) return false;
      if (filter.dateTo   && t.date > filter.dateTo)   return false;
      return true;
    }).sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id));
  }, [transactions, categories, mainCurrency, filter.type, filter.currency, filter.search, filter.dateFrom, filter.dateTo]);

  useEffect(() => { setPage(1); }, [filter.type, filter.currency, filter.search, filter.dateFrom, filter.dateTo]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleTxns = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const showInlineForm = !isPhone || !!editId;

  return (
    <div>
      {showInlineForm && (
      <>
      {/* Quick Add Form */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">{editId ? 'Edit transaction' : 'Log transaction'}</span>
          <div className="flex">
            {editId && <button className="btn btn-sm" onClick={cancelEdit}><X size={12} /> Cancel edit</button>}
            <button className="btn btn-sm" onClick={() => setShowPasteImport(true)} title="Import from bank statement">
              <ClipboardPaste size={13} /> Import
            </button>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowHelp(true)} title="Keyboard shortcuts">
              <HelpCircle size={14} />
            </button>
          </div>
        </div>

        {/* Row 1 */}
        <div className="tracker-row1" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ flex: '0 0 150px' }}>
            <label>Date <span className="kbd">T</span> <span className="kbd">Y</span></label>
            <input id="date-input" ref={dateRef} type="date" value={form.date}
              onChange={e => setF('date', e.target.value)} className={errors.date ? 'input-error' : ''} />
            {errors.date && <div className="error-msg">{errors.date}</div>}
          </div>

          <div style={{ flex: '0 0 150px' }}>
            <label>Type <span className="kbd">1</span> <span className="kbd">2</span> <span className="kbd">3</span></label>
            <select value={form.type} onChange={e => { setF('type', e.target.value); setF('category', ''); }}>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          {form.type !== 'savings' && (
            <div style={{ flex: 1, minWidth: 140 }}>
              <label>Category</label>
              <CategorySelect
                value={form.category}
                onChange={id => setF('category', id)}
                categories={categories}
                yearlyExpenses={yearlyExpenses}
                type={form.type === 'income' ? 'income' : 'expense'}
                addYearlyExpense={form.type === 'expense' ? addYearlyExpense : undefined}
                showToast={showToast}
                className={errors.category ? 'input-error' : ''}
                placeholder="Select category…"
              />
              {errors.category && <div className="error-msg">{errors.category}</div>}
            </div>
          )}

          {form.type === 'savings' && (
            <div style={{ flex: 1, minWidth: 140 }}>
              <label>Savings bucket</label>
              <SearchableSelect
                value={form.bucket}
                onChange={id => {
                  setF('bucket', id);
                  const picked = buckets.find(b => b.id === id);
                  const bucketCcy = picked?.currency || mainCurrency;
                  setForm(p => ({
                    ...p,
                    bucket: id,
                    currency: bucketCcy,
                    fromBalance: bucketCcy === mainCurrency ? false : p.fromBalance,
                    exchangeRate: bucketCcy === mainCurrency ? '' : p.exchangeRate,
                  }));
                }}
                options={buckets.filter(b => !b.archived).map(b => ({
                  value: b.id,
                  label: b.name,
                  secondary: b.currency && b.currency !== mainCurrency ? b.currency : undefined,
                }))}
                placeholder="Select bucket…"
                className={errors.bucket ? 'input-error' : ''}
              />
              {errors.bucket && <div className="error-msg">{errors.bucket}</div>}
            </div>
          )}
        </div>

        {/* Row 2: Description · Amount · Currency · Add */}
        <div className="tracker-row2" style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label>Description</label>
            <DescriptionAutocomplete
              value={form.description}
              onChange={v => setF('description', v)}
              descIndex={descIndex}
              category={form.category}
              type={form.type}
              placeholder="What did you buy/earn?"
              className={errors.description ? 'input-error' : ''}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            />
            {errors.description && <div className="error-msg">{errors.description}</div>}
          </div>
          <div style={{ flex: '0 0 120px' }}>
            <label style={{ visibility: 'hidden' }}>Amount</label>
            <input id="amount-input" ref={amountRef} type="number" value={form.amount}
              onChange={e => setF('amount', e.target.value)}
              placeholder="0.00" min="0" step="0.01"
              className={errors.amount ? 'input-error' : ''}
              style={{ fontFamily: 'var(--font-mono)' }}
              onKeyDown={e => e.key === 'Enter' && submit()} />
            {errors.amount && <div className="error-msg">{errors.amount}</div>}
          </div>
          <div style={{ flex: '0 0 120px' }}>
            <label>Currency</label>
            {(() => {
              const savingsBucket = form.type === 'savings' ? buckets.find(b => b.id === form.bucket) : null;
              const lockedToBucket = savingsBucket?.currency && savingsBucket.currency !== mainCurrency;
              if (lockedToBucket) {
                return (
                  <input
                    readOnly
                    value={form.currency}
                    title={`${savingsBucket.name} is denominated in ${form.currency}`}
                    style={{ fontFamily: 'var(--font-mono)', background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                  />
                );
              }
              return (
                <SearchableSelect
                  value={form.currency}
                  onChange={code => { setF('currency', code); setF('fromBalance', false); setF('exchangeRate', ''); }}
                  options={getAllCurrencies().map(c => ({
                    value: c.code,
                    label: c.code,
                    secondary: c.symbol,
                  }))}
                  placeholder="Currency…"
                />
              );
            })()}
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label>Tags <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>(comma-separated)</span></label>
            <input
              value={form.tags || ''}
              onChange={e => setF('tags', e.target.value)}
              placeholder="e.g. work, reimbursable"
              onKeyDown={e => e.key === 'Enter' && submit()}
            />
          </div>
          <button className="btn btn-primary" onClick={submit} style={{ flexShrink: 0 }}>
            {editId ? <><Check size={14} /> Update</> : <><Plus size={14} /> Add <span className="kbd" style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)' }}>↵</span></>}
          </button>
          {!editId && form.description && form.amount && parseFloat(form.amount) > 0 && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              title="Save as a recurring template (shown each month)"
              onClick={() => {
                addRecurringTemplate({
                  type: form.type, category: form.category,
                  description: form.description.trim(),
                  amount: parseFloat(form.amount),
                  bucket: form.bucket || null,
                });
                showToast(`"${form.description.trim()}" saved as recurring`);
              }}
            >
              ↺ Recurring
            </button>
          )}
        </div>

        {/* Row 3: Foreign currency — compact single-line strip */}
        {form.currency !== mainCurrency && (() => {
          const matchBal = currencyBalances.find(b => b.currency === form.currency);
          const { totalForeign: balTotal, avgRate } = matchBal
            ? computeBalanceCostBasis(matchBal)
            : { totalForeign: 0, avgRate: 0 };
          const fc       = getCurrencyInfo(form.currency);
          const symb     = fc?.symbol || form.currency;
          const effRate  = form.fromBalance ? avgRate : (parseFloat(form.exchangeRate) || 0);
          const nisEquiv = (parseFloat(form.amount) || 0) * effRate;
          const sourceOptions = matchBal && balTotal > 0 ? [
            { value: 'balance',  label: `From ${form.currency} balance (${symb}${balTotal.toFixed(2)}, avg ${avgRate.toFixed(3)})` },
            { value: 'exchange', label: 'New exchange' },
          ] : null;
          return (
            <div className="tracker-fx-strip" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)' }}>
              {/* Source picker — SearchableSelect when balance available, plain label otherwise */}
              {sourceOptions ? (
                <div style={{ width: 280, flexShrink: 0 }}>
                  <SearchableSelect
                    value={form.fromBalance ? 'balance' : 'exchange'}
                    onChange={v => setF('fromBalance', v === 'balance')}
                    options={sourceOptions}
                    placeholder="Source…"
                  />
                </div>
              ) : (
                <span className="muted" style={{ fontSize: 12, flexShrink: 0 }}>Rate ({settings.currency}/{symb}):</span>
              )}

              {/* Rate input — only for new exchange or when no balance */}
              {!form.fromBalance && (
                <input
                  type="number" value={form.exchangeRate}
                  onChange={e => setF('exchangeRate', e.target.value)}
                  placeholder={`${settings.currency}/${symb}`}
                  min="0" step="0.01"
                  style={{ width: 100, fontFamily: 'var(--font-mono)', fontSize: 13 }}
                />
              )}

              {/* Equivalent */}
              {nisEquiv > 0 && (
                <span className="mono income-text" style={{ fontSize: 13, marginLeft: 'auto' }}>= {fmt(nisEquiv)}</span>
              )}
            </div>
          );
        })()}
      </div>
      </>
      )}

      {/* Recurring templates — due this month */}
      {recurringTemplates && recurringTemplates.length > 0 && (() => {
        const todayISO = todayStr();
        const thisMonth = todayISO.slice(0, 7);
        const due = recurringTemplates.filter(t => {
          if (!t.lastAdded) return true;
          return t.lastAdded.slice(0, 7) < thisMonth;
        });
        if (due.length === 0) return null;
        return (
          <div className="card recurring-card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <span className="card-title">Recurring — due this month</span>
              <span className="muted" style={{ fontSize: 12 }}>{due.length} pending</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 0 4px' }}>
              {due.map(t => {
                const cat = categories.find(c => c.id === t.category);
                const typeColor = t.type === 'income' ? 'var(--income)' : t.type === 'savings' ? 'var(--savings)' : 'var(--expense)';
                const addNow = () => {
                  addTransaction({ date: todayISO, type: t.type, category: t.category, description: t.description, amount: t.amount, bucket: t.bucket || null });
                  updateRecurringTemplate(t.id, { lastAdded: todayISO });
                  showToast(`${t.description} added`);
                };
                const addBtn = (
                  <button className="btn btn-sm btn-primary" title="Add this transaction for today" onClick={addNow}>
                    <Plus size={12} /> Add
                  </button>
                );
                const delBtn = (
                  <button className="btn btn-ghost btn-icon btn-xs" title="Remove recurring template" onClick={() => deleteRecurringTemplate(t.id)}>
                    <Trash2 size={12} />
                  </button>
                );

                if (isPhone) {
                  return (
                    <ListRow
                      key={t.id}
                      leading={<span className="txn-dot" style={{ background: typeColor }} />}
                      title={t.description}
                      sub={<>{cat ? `${cat.name} · ` : ''}{fmt(t.amount)}</>}
                      trailing={<div className="recurring-actions">{addBtn}{delBtn}</div>}
                    />
                  );
                }
                return (
                  <div key={t.id} className="recurring-row">
                    <span className={`badge badge-${t.type}`}>{t.type}</span>
                    <span style={{ flex: 1, fontSize: 13 }}>{t.description}</span>
                    {cat && <span className="muted" style={{ fontSize: 12 }}>{cat.name}</span>}
                    <span className="mono" style={{ fontSize: 13 }}>{fmt(t.amount)}</span>
                    {addBtn}
                    {delBtn}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      {isPhone ? (
        <div className="card" style={{ marginBottom: 16, padding: 12 }}>
          <div className="txn-filter-bar">
            <div className="txn-search-wrap">
              <Search size={15} className="txn-search-icon" />
              <input
                className="txn-search"
                value={filter.search}
                onChange={e => setFilter(p => ({ ...p, search: e.target.value }))}
                placeholder="Search…"
              />
            </div>
            <button
              className={`btn txn-filter-btn${activeFilterCount > 0 ? ' has-active' : ''}`}
              onClick={() => setShowFilterSheet(true)}
            >
              <SlidersHorizontal size={14} /> Filters{activeFilterCount > 0 ? ` • ${activeFilterCount}` : ''}
            </button>
          </div>
        </div>
      ) : (
        <div className="card tracker-filters-card" style={{ marginBottom: 16, padding: '12px 16px' }}>
          <div className="tracker-filters" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="tf-type">
              <label>Type filter</label>
              <select aria-label="Type filter" value={filter.type} onChange={e => setFilter(p => ({ ...p, type: e.target.value }))} style={{ width: 140 }}>
                <option value="">All types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
                <option value="savings">Savings</option>
              </select>
            </div>
            <div className="tf-search" style={{ flex: 1, minWidth: 160 }}>
              <label>Search</label>
              <input value={filter.search} onChange={e => setFilter(p => ({ ...p, search: e.target.value }))} placeholder="Search by description..." />
            </div>
            <div className="tf-currency">
              <label>Currency</label>
              <select aria-label="Currency filter" value={filter.currency} onChange={e => setFilter(p => ({ ...p, currency: e.target.value }))} style={{ width: 110 }}>
                <option value="">All currencies</option>
                {[...new Set(transactions.map(t => t.currency || mainCurrency))].sort().map(currency => <option key={currency} value={currency}>{currency}</option>)}
              </select>
            </div>
            <div className="tf-from">
              <label>From</label>
              <input type="date" value={filter.dateFrom} onChange={e => setFilter(p => ({ ...p, dateFrom: e.target.value }))} style={{ width: 140 }} />
            </div>
            <div className="tf-to">
              <label>To</label>
              <input type="date" value={filter.dateTo} onChange={e => setFilter(p => ({ ...p, dateTo: e.target.value }))} style={{ width: 140 }} />
            </div>
            {((filter.type || filter.currency || filter.search || filter.dateFrom || filter.dateTo)) && (
              <button className="btn btn-sm tf-clear" onClick={() => setFilter({ type: String(), currency: String(), search: String(), dateFrom: String(), dateTo: String() })}>
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Transaction history</span>
          <span className="muted" style={{ fontSize: 12 }}>
            {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
            {totalPages > 1 && ` · page ${currentPage} of ${totalPages}`}
          </span>
        </div>

        {/* Bulk action toolbar (desktop only — phone uses tap/swipe) */}
        {!isPhone && selected.size > 0 && (
          <div className="bulk-toolbar">
            <span className="muted" style={{ fontSize: 13 }}>{selected.size} selected</span>
            <div style={{ flex: 1, minWidth: 140 }}>
              <SearchableSelect
                value={bulkRecatId}
                onChange={id => {
                  setBulkRecatId(id);
                  selected.forEach(sid => updateTransaction(sid, { category: id }));
                  showToast(`Re-categorized ${selected.size} transactions`);
                  clearSelection();
                }}
                options={categories.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Re-categorize to…"
              />
            </div>
            <button className="btn btn-sm btn-danger" onClick={() => setConfirmBulkDelete(true)}>
              <Trash2 size={12} /> Delete {selected.size}
            </button>
            <button className="btn btn-sm btn-ghost" onClick={clearSelection}>
              <X size={12} /> Clear
            </button>
          </div>
        )}

        {filtered.length === 0
          ? <EmptyState icon={<ClipboardList size={28} />} title="No transactions yet" sub={isPhone ? 'Tap the + button to log your first transaction' : 'Use the form above to log your first transaction'} />
          : isPhone
          ? (
            <CardList
              className="txn-list"
              rows={visibleTxns}
              rowKey={t => t.id}
              onTap={t => openEntrySheet(t)}
              columns={[
                { slot: 'leading', render: t => {
                    const bucket = buckets.find(b => b.id === t.bucket);
                    if (t.type === 'savings' && bucket?.emoji) return bucket.emoji;
                    const typeColor = t.type === 'income' ? 'var(--income)' : t.type === 'savings' ? 'var(--savings)' : 'var(--expense)';
                    return <span className="txn-dot" style={{ background: typeColor }} />;
                  } },
                { slot: 'title', render: t => t.description },
                { slot: 'sub', render: t => {
                    const bucket = buckets.find(b => b.id === t.bucket);
                    const catDisplay = t.type === 'savings'
                      ? (bucket ? bucket.name : '—')
                      : resolveCategoryName(t.category, categories, yearlyExpenses);
                    return (
                      <>
                        {catDisplay} · {formatDate(t.date, settings)}
                        {t.tags && t.tags.length > 0 && (
                          <span className="txn-tags">
                            {t.tags.map(tag => <span key={tag} className="txn-tag">{tag}</span>)}
                          </span>
                        )}
                      </>
                    );
                  } },
                { slot: 'value', render: t => (
                    <>
                      {t.type === 'expense' ? '−' : '+'}{fmt(t.amount)}
                      {t.foreignAmount && (
                        <span className="list-row-value-sub">
                          {getCurrencyInfo(t.currency)?.symbol || t.currency}{t.foreignAmount.toFixed(2)}
                        </span>
                      )}
                    </>
                  ), valueClass: t => t.type === 'income' ? 'income-text' : t.type === 'savings' ? 'savings-text' : 'expense-text' },
              ]}
              slideLeft={[{
                label: 'Delete', icon: <Trash2 size={16} />, color: 'danger',
                onAction: t => setConfirmDelete(t),
              }]}
            />
          )
          : (
            <div className="table-wrapper">
              <table className="table-txn">
                <thead>
                  <tr>
                    <th style={{ width: 28, paddingRight: 0 }}>
                      <input
                        type="checkbox"
                        checked={visibleTxns.length > 0 && visibleTxns.every(t => selected.has(t.id))}
                        onChange={toggleSelectAll}
                        title="Select all on this page"
                      />
                    </th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTxns.map(t => {
                    const bucket    = buckets.find(b => b.id === t.bucket);
                    const catDisplay = t.type === 'savings'
                      ? (bucket ? bucket.name : '—')
                      : resolveCategoryName(t.category, categories, yearlyExpenses);
                    return (
                      <tr key={t.id} style={editId === t.id ? { background: 'var(--accent-bg)' } : selected.has(t.id) ? { background: 'var(--savings-bg)' } : {}}>
                        <td style={{ width: 28, paddingRight: 0 }}>
                          <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} />
                        </td>
                        <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(t.date, settings)}</td>
                        <td><span className={`badge badge-${t.type}`}>{t.type}</span></td>
                        <td>{catDisplay}</td>
                        <td style={{ color: 'var(--text-secondary)', maxWidth: 220 }}>
                          {t.description}
                          {t.tags && t.tags.length > 0 && (
                            <div className="txn-tags">
                              {t.tags.map(tag => <span key={tag} className="txn-tag">{tag}</span>)}
                            </div>
                          )}
                        </td>
                        <td className={`amount-cell ${t.type === 'income' ? 'income-text' : t.type === 'savings' ? 'savings-text' : 'expense-text'}`}>
                          {t.type === 'expense' ? '−' : '+'}{fmt(t.amount)}
                          {t.foreignAmount && (
                            <div className="muted mono" style={{ fontSize: 10 }}>
                              {getCurrencyInfo(t.currency)?.symbol || t.currency}{t.foreignAmount.toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="flex flex-end" style={{ gap: 4 }}>
                            <button className="btn btn-ghost btn-icon btn-xs" onClick={() => startEdit(t)} title="Edit"><Edit2 size={12} /></button>
                            <button className="btn btn-ghost btn-icon btn-xs btn-danger" onClick={() => setConfirmDelete(t)} title="Delete"><Trash2 size={12} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        {totalPages > 1 && (
          <Paginator page={currentPage} totalPages={totalPages} onChange={setPage} />
        )}
      </div>

      {/* Filter bottom sheet (phone) */}
      {showFilterSheet && (
        <Modal title="Filters" variant="sheet" onClose={() => setShowFilterSheet(false)}>
          <div className="form-group">
            <label>Type</label>
            <select aria-label="Mobile type filter" value={filter.type} onChange={e => setFilter(p => ({ ...p, type: e.target.value }))}>
              <option value="">All types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="savings">Savings</option>
            </select>
          </div>
          <div className="form-group">
            <label>Currency</label>
            <select aria-label="Mobile currency filter" value={filter.currency} onChange={e => setFilter(p => ({ ...p, currency: e.target.value }))}>
              <option value="">All currencies</option>
              {[...new Set([mainCurrency, ...transactions.map(t => t.currency || mainCurrency), ...(currencyBalances || []).map(b => b.currency)])].sort().map(currency => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>From</label>
            <input type="date" value={filter.dateFrom} onChange={e => setFilter(p => ({ ...p, dateFrom: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>To</label>
            <input type="date" value={filter.dateTo} onChange={e => setFilter(p => ({ ...p, dateTo: e.target.value }))} />
          </div>
          <div className="flex" style={{ gap: 8, marginTop: 12 }}>
            <button
              className="btn flex-1"
              onClick={() => setFilter(p => ({ type: String(), currency: String(), search: p.search, dateFrom: String(), dateTo: String() }))}
            >
              <X size={12} /> Clear filters
            </button>
            <button className="btn btn-primary flex-1" onClick={() => setShowFilterSheet(false)}>Done</button>
          </div>
        </Modal>
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete transaction"
          message={`Delete "${confirmDelete.description}" (${fmt(confirmDelete.amount)})? This cannot be undone.`}
          danger
          onConfirm={() => { deleteTransaction(confirmDelete.id); setConfirmDelete(null); showToast('Transaction deleted'); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Confirm Bulk Delete */}
      {confirmBulkDelete && (
        <ConfirmDialog
          title={`Delete ${selected.size} transactions`}
          message={`Permanently delete ${selected.size} selected transactions? This cannot be undone.`}
          danger
          onConfirm={() => {
            selected.forEach(id => deleteTransaction(id));
            showToast(`${selected.size} transactions deleted`);
            clearSelection();
            setConfirmBulkDelete(false);
          }}
          onCancel={() => setConfirmBulkDelete(false)}
        />
      )}

      {/* Import Modal */}
      {showPasteImport && (
        <PasteImport
          categories={categories}
          yearlyExpenses={yearlyExpenses}
          addYearlyExpense={addYearlyExpense}
          catIndex={catIndex}
          settings={settings}
          currencyBalances={currencyBalances}
          onAdd={txns => {
            addTransactions(txns);
            showToast(`${txns.length} transaction${txns.length !== 1 ? 's' : ''} added`);
          }}
          onClose={() => setShowPasteImport(false)}
        />
      )}

      {/* Keyboard Help Modal */}
      {showHelp && (
        <Modal title="Keyboard shortcuts" onClose={() => setShowHelp(false)}>
          <table style={{ fontSize: 13 }}>
            <tbody>
              {[
                ['T', 'Set date to today (in date field)'],
                ['Y', 'Set date to yesterday (in date field)'],
                ['1', 'Select Income type'],
                ['2', 'Select Expense type'],
                ['3', 'Select Savings type'],
                ['Alt+G', 'Quick select Groceries'],
                ['Alt+T', 'Quick select Transport'],
                ['Alt+U', 'Quick select Utilities'],
                ['Alt+F', 'Quick select Dining Out'],
                ['Alt+E', 'Quick select Entertainment'],
                ['Enter', 'Submit transaction'],
                ['Esc', 'Clear form'],
              ].map(([k, d]) => (
                <tr key={k}>
                  <td style={{ paddingRight: 16, paddingBottom: 8 }}><span className="kbd">{k}</span></td>
                  <td style={{ paddingBottom: 8, color: 'var(--text-secondary)' }}>{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}

/* ─── Description Autocomplete ─── */
/**
 * Shows a ranked dropdown of past descriptions filtered by the current category+type.
 * Rank = frequency descending; ties broken by recency.
 * The badge "(1)", "(2)"… shows the frequency rank within the current scope.
 */
function DescriptionAutocomplete({ value, onChange, descIndex, category, type, placeholder, className, onKeyDown }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef();
  const listRef  = useRef();

  // O(1) lookup — the heavy scan already happened in the parent's useMemo.
  // Prefer scoped key (type:category); fall back to type-only when no category chosen.
  const suggestions = descIndex[`${type}:${category}`] || descIndex[`${type}:`] || [];

  // Filter by what the user has typed — cheap, runs on query change only
  const filtered = useMemo(() => {
    if (!query.trim()) return suggestions.slice(0, 8);
    const q = query.toLowerCase();
    return suggestions.filter(s => s.desc.toLowerCase().includes(q)).slice(0, 8);
  }, [suggestions, query]);

  function handleFocus() {
    setQuery('');
    setOpen(true);
  }

  function handleBlur() {
    setTimeout(() => {
      if (!listRef.current?.contains(document.activeElement)) {
        setOpen(false);
        setQuery('');
      }
    }, 150);
  }

  function pick(desc) {
    onChange(desc);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  }

  function handleChange(e) {
    setQuery(e.target.value);
    onChange(e.target.value);
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
    if (e.key === 'Enter' && open && filtered.length > 0) {
      // If the current value exactly matches a suggestion, let submit fire
      const exact = filtered.find(s => s.desc.toLowerCase() === value.toLowerCase());
      if (!exact) {
        e.preventDefault();
        pick(filtered[0].desc);
        return;
      }
    }
    onKeyDown?.(e);
  }

  // Display: when open show query, else show the committed value
  const displayValue = open ? query : value;

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={open ? 'Type to filter past descriptions…' : (placeholder || 'Description')}
        maxLength={100}
        className={className}
        style={{ width: '100%' }}
        autoComplete="off"
        spellCheck={false}
      />
      {open && filtered.length > 0 && (
        <div
          ref={listRef}
          style={{
            position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0,
            zIndex: 9999,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            maxHeight: 280,
            overflowY: 'auto',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {filtered.map(s => (
            <div
              key={s.desc}
              className="ss-option"
              onMouseDown={e => { e.preventDefault(); pick(s.desc); }}
            >
              <span
                style={{
                  minWidth: 22, height: 18, borderRadius: 10,
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  fontSize: 10, fontFamily: 'var(--font-mono)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-tertiary)', flexShrink: 0,
                }}
              >
                {s.rank}
              </span>
              <span style={{ flex: 1 }}>{s.desc}</span>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                ×{s.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Paginator ─── */
function Paginator({ page, totalPages, onChange }) {
  // Build the page number list: always show first, last, current ±2, with ellipsis gaps
  function pageNumbers() {
    const delta = 2;
    const range = [];
    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }
    const pages = [1];
    if (range[0] > 2) pages.push('…');
    range.forEach(n => pages.push(n));
    if (range[range.length - 1] < totalPages - 1) pages.push('…');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  }

  const btnBase = {
    minWidth: 32, height: 30, borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)', background: 'transparent',
    cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-mono)',
    color: 'var(--text-secondary)', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', padding: '0 6px',
    transition: 'background var(--transition)',
  };
  const btnActive = {
    ...btnBase,
    background: 'var(--text-primary)', color: 'var(--bg-primary)',
    border: '1px solid var(--text-primary)', fontWeight: 600,
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
      <button
        style={btnBase}
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        title="Previous page"
      >
        <ChevronLeft size={13} />
      </button>

      {pageNumbers().map((n, i) =>
        n === '…'
          ? <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: 'var(--text-tertiary)', fontSize: 13 }}>…</span>
          : <button key={n} style={n === page ? btnActive : btnBase} onClick={() => onChange(n)}>{n}</button>
      )}

      <button
        style={btnBase}
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        title="Next page"
      >
        <ChevronRight size={13} />
      </button>
    </div>
  );
}

export default React.memo(Tracker);
